import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type LayoutChangeEvent, StyleSheet, Text } from 'react-native';
import {
  ActiveIngredient,
  getCloudinaryUrl,
  Image,
  theme,
  useActiveIngredientFloatingStepper,
  useBrowserEventListener,
  useHovered,
  useToggleState,
} from '@sg/garnish';

import { CustomizationDietaryRestrictions } from '../CustomizationDietaryRestrictions';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationSelectedIngredient = <
  IngredientType extends IngredientBase,
>(
  props: CustomizationSelectedIngredientProps<IngredientType>,
) => {
  const {
    ingredientModification,
    restrictionNotice,
    dietaryRestrictions,
    isActive,
    isDisabled,
    imageWidth = 230,
    setActive,
    setInactive,
    onRemove,
    onIncreaseQuantity,
    onDecreaseQuantity,
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  const [triggerHeight, setTriggerHeight] = useState(0);

  const { triggerRef, triggerCoordinates, updateStepperTriggerCoordinates } =
    useActiveIngredientFloatingStepper();

  const isHovered = useHovered(triggerRef);

  const { formatMessage } = useIntl();

  // ─── Dietary Restrictions ────────────────────────────────────────────

  const {
    value: isShowingDietaryRestrictionModal,
    toggleOn: showDietaryRestrictionModal,
    toggleOff: hideDietaryRestrictionModal,
  } = useToggleState();

  const dietaryRestrictionMessage = formatMessage(
    messages.dietaryRestrictionMessage,
    { restriction: dietaryRestrictions },
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleTriggerLayout = useCallback((event: LayoutChangeEvent) => {
    setTriggerHeight(event.nativeEvent.layout.height);
  }, []);

  const handleOnPress = useCallback(() => {
    updateStepperTriggerCoordinates();
    setActive(ingredientModification.ingredient.id);
  }, [
    updateStepperTriggerCoordinates,
    ingredientModification.ingredient.id,
    setActive,
  ]);

  const handleIncreaseQuantity = useCallback(() => {
    onIncreaseQuantity(ingredientModification);
  }, [ingredientModification, onIncreaseQuantity]);

  const handleDecreaseQuantity = useCallback(() => {
    onDecreaseQuantity(ingredientModification);
  }, [ingredientModification, onDecreaseQuantity]);

  const handleRemove = useCallback(() => {
    onRemove(ingredientModification);
  }, [ingredientModification, onRemove]);

  const handleDismissOnEsc = useCallback(
    (event: Event) => {
      const castedEvent = event as KeyboardEvent;

      if (castedEvent.key !== 'Escape') return;

      setInactive();
    },
    [setInactive],
  );

  // ─────────────────────────────────────────────────────────────────────

  const ingredientImageSource = ingredientModification.ingredient?.asset?.url;
  const ingredientImage = useMemo(() => {
    if (!ingredientImageSource) return null;

    return getCloudinaryUrl(ingredientImageSource, {
      crop: 'scale',
      width: imageWidth,
    });
  }, [imageWidth, ingredientImageSource]);

  const ingredientName = ingredientModification.ingredient.name;
  const quantity = ingredientModification.quantity ?? 1;

  // ─── a11y labels ─────────────────────────────────────────────────────

  const openQuantityStepperA11yLabel = formatMessage(
    messages.openQuantityStepper,
    { ingredient: ingredientName },
  );
  const closeQuantityStepperA11yLabel = formatMessage(
    messages.closeQuantityStepper,
  );
  const increaseQty = formatMessage(messages.increaseQty, {
    ingredient: ingredientName,
    quantity,
  });
  const decreaseQty = formatMessage(messages.decreaseQty, {
    ingredient: ingredientName,
    quantity,
  });
  const removeIngredient = formatMessage(messages.removeIngredient, {
    ingredient: ingredientName,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useBrowserEventListener(
    window,
    'keydown',
    handleDismissOnEsc,
    undefined,
    !isActive,
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <ActiveIngredient.Container>
        <ActiveIngredient.PressableContainer
          ref={triggerRef}
          onLayout={handleTriggerLayout}
        >
          <ActiveIngredient.Pressable
            active={isActive}
            hovered={isHovered}
            onPress={handleOnPress}
            accessibilityLabel={openQuantityStepperA11yLabel}
            disabled={isDisabled}
          >
            {ingredientImage ? (
              <Image source={ingredientImage} style={styles.image} />
            ) : null}

            <ActiveIngredient.Text active={isActive} hovered={isHovered}>
              {quantity > 1 ? (
                <>
                  {ingredientModification.quantity}
                  <Text> </Text>
                </>
              ) : null}

              {ingredientModification.ingredient.name}
            </ActiveIngredient.Text>
          </ActiveIngredient.Pressable>

          <ActiveIngredient.RemoveButton
            active={isActive}
            hovered={isHovered}
            accessibilityLabel={removeIngredient}
            onPress={handleRemove}
          />

          <ActiveIngredient.QuantityStepper.Overlay
            isVisible={isActive}
            stepperPositionX={triggerCoordinates.x}
            stepperPositionY={triggerCoordinates.y}
            stepperOffsetY={triggerHeight}
            dismiss={setInactive}
            accessibilityLabel={closeQuantityStepperA11yLabel}
          >
            <ActiveIngredient.QuantityStepper.Container isVisible={isActive}>
              <ActiveIngredient.QuantityStepper.Button
                name="IconMinus2"
                onPress={handleDecreaseQuantity}
                accessibilityLabel={
                  quantity === 1 ? removeIngredient : decreaseQty
                }
              />

              <ActiveIngredient.QuantityStepper.Text>
                {ingredientModification.quantity}
              </ActiveIngredient.QuantityStepper.Text>

              <ActiveIngredient.QuantityStepper.Button
                name="IconPlus2"
                onPress={handleIncreaseQuantity}
                accessibilityLabel={increaseQty}
                autoFocus={isActive}
              />
            </ActiveIngredient.QuantityStepper.Container>
          </ActiveIngredient.QuantityStepper.Overlay>
        </ActiveIngredient.PressableContainer>

        {restrictionNotice ? (
          <CustomizationDietaryRestrictions.Info
            restrictionNotice={restrictionNotice}
            onRequestModal={showDietaryRestrictionModal}
          />
        ) : null}
      </ActiveIngredient.Container>

      <CustomizationDietaryRestrictions.Modal
        visible={isShowingDietaryRestrictionModal}
        restrictionNotice={dietaryRestrictionMessage}
        onRequestClose={hideDietaryRestrictionModal}
      />
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  image: {
    width: 30,
    height: 30,
    borderRadius: 30,
    backgroundColor: theme.colors.LIGHT,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  openQuantityStepper: {
    defaultMessage: 'Open quantity stepper for {ingredient}',
    description:
      'Customization | Ingredient | Open quantity stepper a11y label',
  },
  closeQuantityStepper: {
    defaultMessage: 'Close quantity stepper',
    description:
      'Customization | Ingredient | Close quantity stepper a11y label',
  },
  increaseQty: {
    defaultMessage:
      'Increase quantity of {ingredient} (current quantity: {quantity})',
    description: 'Customization | Ingredient | Increase quantity a11y label',
  },
  decreaseQty: {
    defaultMessage:
      'Decrease quantity of {ingredient} (current quantity: {quantity})',
    description: 'Customization | Ingredient | Decrease quantity a11y label',
  },
  removeIngredient: {
    defaultMessage: 'Remove {ingredient}',
    description: 'Customization | Ingredient | Remove ingredient a11y label',
  },
  dietaryRestrictionMessage: {
    defaultMessage: 'Please note — this ingredient contains {restriction}.',
    description: 'Customization | Ingredient | Dietary restriction notice',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientProps<
  IngredientType extends IngredientBase,
> = {
  ingredientModification: IngredientType;
  isActive: boolean;
  isDisabled?: boolean;
  restrictionNotice?: string;
  dietaryRestrictions?: string;
  imageWidth?: number;
  setActive: (ingredientId: string) => void;
  setInactive: () => void;
  onRemove: (ingredient: IngredientType) => void;
  onIncreaseQuantity: (ingredient: IngredientType) => void;
  onDecreaseQuantity: (ingredient: IngredientType) => void;
};

type IngredientBase = {
  ingredient: { id: string; name: string; asset: IngredientAsset };
  quantity?: number | null;
};

type IngredientAsset = {
  url: string;
};
